// @flow

import React, { Fragment } from 'react';
import Hammer from 'hammerjs';
import { I18n, Translate } from 'react-redux-i18n';
import { Link } from 'react-router-dom';
import type { CoqueRetailer, GoodwillRatioType, GoodwillType, Store } from '../../../../../types/apolloType';
import { trackPageMap } from '../../../../../components/GoogleAnalyticsWrapper';
import visioGlobeApi from '../../../../../network/api/visioGlobeApi';
import Mapviewer from '../../../../mall/map/mapviewer';
import { autoQuantityCurrencyString, roundPercentage } from '../../../../../utils/formatUtils';
import { getCurrentPcId } from '../../../../../utils/urlUtils';
import { GOODWILL_RATIOS, isMobile } from '../../../../../const';
import BaseMap from '../../../../mall/map/BaseMap';
import BaseMapOverlay from '../../../../mall/map/BaseMapOverlay';
import GoodwillBaseOverlay from './GoodwillBaseOverlay';
import type { GeminiRights, ShoppingCenter } from '../../../../../types/shoppingCenterType';
import EllipsisLoader from '../../../../../components/EllipsisLoader';
import GoodwillButtonBestRetailer from './GoodwillButtonBestRetailer';
import GoodwillRatioButton from './GoodwillRatioButton';
import storesApi from '../../../../../network/api/storesApi';

type Props = {
  dispatch: () => void,
  shoppingCenter: ShoppingCenter,
  currentRetailer: CoqueRetailer,
  topRetailerIds: Array<number>,
  oldRetailer: Store,
  userGeminiRights: GeminiRights,
};

type State = {
  mapReady: boolean,
  noMap: boolean,
  selectedRatioName: string,
  goodwill: GoodwillType,
  isGoodwillLoading: boolean,
};

class Goodwill extends React.Component<Props, State> {
  state: State = {
    mapReady: false,
    noMap: false,
    selectedRatioName: GOODWILL_RATIOS.PROXIMITY,
    goodwill: null,
    isGoodwillLoading: true,
  };

  mapviewer: Mapviewer;
  ref;
  scrollRef;

  componentDidMount() {
    const pcId = this.props.shoppingCenter.id;

    this.fetchGoodwill(pcId, this.props.currentRetailer, this.props.oldRetailer.codstr);
    this.fetchVisioglobe(pcId, this.props.userGeminiRights.isAuthorized);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if (this.props.currentRetailer.codeRetailerProposition !== nextProps.currentRetailer.codeRetailerProposition) {
      this.fetchGoodwill(nextProps.shoppingCenter.id, nextProps.currentRetailer, nextProps.oldRetailer.codstr);
    }

    return true;
  }

  fetchVisioglobe = (pcId: number, isAuthorized: boolean) => {
    this.setState({ mapReady: false });
    trackPageMap(isAuthorized);

    visioGlobeApi.fetchHashmap(pcId)
      .then(response => response.json())
      .then((responseJson: { hashmap: string }) => {
        this.mapviewer = new Mapviewer(
          responseJson.hashmap,
          getCurrentPcId(),
          isMobile,
          false,
          'container',
          () => {
            this.setState({ mapReady: true });
          },
        );
      }).then(() => {
        document.dispatchEvent(new Event('mapviewerready'));
      })
      .catch(() => this.setState({ noMap: true }));
  }

  fetchGoodwill = (pcId: number, currentRetailer: CoqueRetailer, codstr: number) => {
    this.setState({ isGoodwillLoading: true });
    const { glaExclSst } = currentRetailer;

    storesApi.fetchRetailerGoodwill(
      pcId,
      currentRetailer.codeStoreCoque,
      codstr,
      currentRetailer.codeRetailerProposition,
      currentRetailer.retailerName,
      currentRetailer.bl3Name,
      glaExclSst,
      // projectedMgr, projectedSales, trv are divided by glaExclSst
      currentRetailer.projectedMgr.average * glaExclSst,
      currentRetailer.projectedSales.average * glaExclSst,
      currentRetailer.trv.average * glaExclSst,
    ).then(response => response.json())
      .then(response => this.setState({ goodwill: response }))
      .catch(() => this.setState({ goodwill: null }))
      .finally(() => this.setState({ isGoodwillLoading: false }));
  }

  getGoodwillMap = () => {
    if (!this.props.userGeminiRights.isAuthorized) {
      return (
        <div className="raw-map">
          <BaseMapOverlay
            mapviewer={this.mapviewer}
            isGeminiUser={this.props.userGeminiRights.isGeminiUser}
          />
        </div>
      );
    }

    return (
      <GoodwillBaseOverlay
        mapviewer={this.mapviewer}
        scrollableElement={this.scrollRef}
        bestRetailer={this.props.currentRetailer}
        topRetailerIds={this.props.topRetailerIds}
        isGoodwillLoading={this.state.isGoodwillLoading}
        selectedRatioName={this.state.selectedRatioName}
        goodwill={this.state.goodwill}
      />
    );
  }

  changeSelectedRatio = (newSelectedRatio: string) =>
    this.setState({ selectedRatioName: newSelectedRatio });

  getImpactTotalValue = (impactRatio: GoodwillRatioType[]) =>
    this.formatGoodwillData(this.reduceImpactValues(impactRatio));

  reduceImpactValues = (impactRatio: GoodwillRatioType[]) =>
    impactRatio.reduce((acc, data) => acc + data.impactValue, 0);

  getTotalImpactOverSales = () =>
    this.formatGoodwillData(this.reduceImpactValues(this.state.goodwill.proximityRatio.impactedStores)
      + this.reduceImpactValues(this.state.goodwill.systematicRatio.impactedStores)
      + this.reduceImpactValues(this.state.goodwill.synergicRatio.impactedStores));

  formatGoodwillData = (dataToFormat: number) => autoQuantityCurrencyString(dataToFormat, 1);

  render() {
    return (
      <div className="goodwill-container">
        <div className="goodwill-map-container">
          <div
            ref={(ref) => {
              this.scrollRef = ref;
            }}
            className="mall-map-container scrollable-content"
          >
            {!this.state.noMap ?
              (
                <div className="mapviewer-container">
                  <div id="vg_mapviewer">
                    <div
                      ref={(ref) => {
                        if (isMobile && !this.ref && ref) {
                          this.ref = ref;
                          const hammertime = new Hammer(ref);
                          hammertime.get('pinch').set({ enable: true });
                        }
                      }}
                      id="container"
                      className="visiomap"
                    />
                    <div id="progress">
                      <input
                        value="0"
                        data-width="150"
                        data-readonly="true"
                        data-thickness=".2"
                        className="knob"
                        readOnly="readonly"
                      />
                    </div>
                  </div>
                  {
                    this.state.mapReady && (
                      <BaseMap
                        scrollableElement={this.scrollRef}
                        isGeminiAuthorized={this.props.userGeminiRights.isAuthorized}
                        isGeminiUser={this.props.userGeminiRights.isGeminiUser}
                        mapviewer={this.mapviewer}
                        overlay={this.getGoodwillMap()}
                      />
                    )
                  }
                </div>
              ) : (
                <div className="no-map-container">
                  <div className="no-gallery">
                    <div className="text">
                      <Translate value="mallMap.NO_MAP" />
                    </div>
                    <i className="fa fa-exclamation-triangle" />
                    <div className="link">
                      <Link href={`/mall/media?mall=${getCurrentPcId()}`} to={`/mall/media?mall=${getCurrentPcId()}`}>
                        <Translate value="mallMap.REDIRECT" /> <i className="fa fa-arrow-right" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div className="goodwill-right-tab">
            {
              this.state.goodwill
                ? (
                  <Fragment>
                    <div className="general-info-container">
                      <div className="right-tab-line">
                        <div className="right-tab-line--left-side">
                          <Translate value="goodwill.attractiveScore.title" />
                          <div className="kpi-description">
                            <Translate value="goodwill.attractiveScore.description" />
                          </div>
                        </div>
                        <div className="right-tab-line--right-side">
                          <span>
                            <b>
                              {I18n.t('goodwill.score', {
                              score: this.state.goodwill.attractiveScore.toFixed(1),
                            })}
                            </b>
                          </span>
                        </div>
                      </div>
                      <div className="right-tab-line">
                        <div className="right-tab-line--left-side">
                          <Translate value="goodwill.gerv.title" />
                          <div className="kpi-description">
                            <Translate value="goodwill.gerv.description" />
                          </div>
                        </div>
                        <div className="right-tab-line--right-side">
                          <span>{this.formatGoodwillData(this.state.goodwill.gerv)}</span>
                        </div>
                      </div>
                      <div className="right-tab-line">
                        <div className="right-tab-line--left-side">
                          <Translate value="goodwill.rentGoodwillReachable.title" />
                          <div className="kpi-description">
                            <Translate value="goodwill.rentGoodwillReachable.description" />
                          </div>
                        </div>
                        <div className="right-tab-line--right-side">
                          <span>{this.formatGoodwillData(this.state.goodwill.rentGoodwillReachable)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="impact-stores-container">
                      <div className="impact-stores-title">
                        <Translate value="goodwill.impact.title" />
                      </div>
                      <GoodwillRatioButton
                        isSelectedRatio={this.state.selectedRatioName === GOODWILL_RATIOS.PROXIMITY}
                        changeSelectedRatio={() => this.changeSelectedRatio(GOODWILL_RATIOS.PROXIMITY)}
                        ratioTitle={<Translate value="goodwill.impact.proximity.title" />}
                        ratioDescription={<Translate value="goodwill.impact.proximity.description" />}
                        ratioValue={this.getImpactTotalValue(this.state.goodwill.proximityRatio.impactedStores)}
                        ratioParenthesises={<Translate
                          className="impact-stores-values--parenthesis"
                          value="goodwill.impact.storeNumber"
                          number={this.state.goodwill.proximityRatio.impactedStores.length}
                        />}
                      />
                      <GoodwillRatioButton
                        isSelectedRatio={this.state.selectedRatioName === GOODWILL_RATIOS.SYSTEMATIC}
                        changeSelectedRatio={() => this.changeSelectedRatio(GOODWILL_RATIOS.SYSTEMATIC)}
                        ratioTitle={<Translate value="goodwill.impact.systematic.title" />}
                        ratioDescription={<Translate value="goodwill.impact.systematic.description" />}
                        ratioValue={this.getImpactTotalValue(this.state.goodwill.systematicRatio.impactedStores)}
                        ratioParenthesises={<Translate
                          className="impact-stores-values--parenthesis"
                          value="goodwill.impact.storeNumber"
                          number={this.state.goodwill.systematicRatio.impactedStores.length}
                        />}
                      />
                      <GoodwillRatioButton
                        isSelectedRatio={this.state.selectedRatioName === GOODWILL_RATIOS.SYNERGIC}
                        changeSelectedRatio={() => this.changeSelectedRatio(GOODWILL_RATIOS.SYNERGIC)}
                        ratioTitle={<Translate value="goodwill.impact.synergic.title" />}
                        ratioDescription={<Translate value="goodwill.impact.synergic.description" />}
                        ratioValue={this.getImpactTotalValue(this.state.goodwill.synergicRatio.impactedStores)}
                        ratioParenthesises={<Translate
                          className="impact-stores-values--parenthesis"
                          value="goodwill.impact.storeNumber"
                          number={this.state.goodwill.synergicRatio.impactedStores.length}
                        />}
                      />
                      <GoodwillRatioButton
                        isSelectedRatio={this.state.selectedRatioName === GOODWILL_RATIOS.TOTAL}
                        changeSelectedRatio={() => this.changeSelectedRatio(GOODWILL_RATIOS.TOTAL)}
                        ratioTitle={<Translate value="goodwill.impact.total.title" />}
                        ratioDescription={<Translate value="goodwill.impact.total.description" />}
                        ratioValue={this.getTotalImpactOverSales()}
                        ratioParenthesises={
                          <span className="impact-stores-values--parenthesis">
                            {I18n.t(
                              'goodwill.impact.mgrPercentage',
                              {
                                percentage: roundPercentage(
                                  this.state.goodwill.totalMgrPercentageOverTotalRatio,
                                  1,
                                ),
                              },
                            )}
                          </span>
                        }
                      />
                      <GoodwillRatioButton
                        isSelectedRatio={this.state.selectedRatioName === GOODWILL_RATIOS.GOODWILL_REACHABLE}
                        changeSelectedRatio={() => this.changeSelectedRatio(GOODWILL_RATIOS.GOODWILL_REACHABLE)}
                        ratioTitle={<Translate value="goodwill.impact.reachable.title" />}
                        ratioDescription={<Translate value="goodwill.impact.reachable.description" />}
                        ratioValue={this.getImpactTotalValue(this.state.goodwill.totalGoodwillReachableAfterCeiling.impactedStores)}
                        ratioParenthesises={
                          <span className="impact-stores-values--parenthesis">
                            {I18n.t(
                              'goodwill.impact.mgrPercentage',
                              {
                                percentage: roundPercentage(
                                  this.state.goodwill.totalMgrPercentageOverTotalGoodwillReachable,
                                  1,
                                ),
                              },
                            )}
                          </span>
                        }
                      />
                    </div>
                    <div className="goodwill-report-container">
                      <GoodwillButtonBestRetailer
                        dispatch={this.props.dispatch}
                        shoppingCenter={this.props.shoppingCenter}
                        currentRetailer={this.props.currentRetailer}
                        oldRetailer={this.props.oldRetailer}
                        buttonName={<Translate value="goodwill.report" />}
                      />
                    </div>
                  </Fragment>
                ) : (
                  <EllipsisLoader />
                )
            }
          </div>
        </div>
        <div className="goodwill-legend">
          <div className="legend-item">
            <div className="legend-item-color na-stores" />
            <Translate value="goodwill.legend.NA" />
          </div>
          <div className="legend-item">
            <div className="legend-item-color selected-store" />
            <Translate value="goodwill.legend.selected" />
          </div>
          <div className="legend-item">
            <div className="legend-item-color locomotive-store" />
            <Translate value="goodwill.legend.locomotive" />
          </div>
        </div>
      </div>
    );
  }
}

export default Goodwill;
